import { Skeleton } from '@/components/ui/skeleton';
import { EntityFormDialog } from '@/shared/components';
import { useStreamForEdit } from '@/features/streams/hooks';
import type { V2Stream } from '@/features/streams/types';

interface StreamViewDialogProps {
  mongoStreamId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Read-only stream viewer. Shows whatever the DB has — no defaults are
 * fabricated. If a field is null/undefined the cell renders `—`.
 *
 * The full editable form lives at `/operations/streams` (StreamFormModal).
 * On the per-account Sensors tab we surface a quick snapshot so users can
 * inspect polynomial / ADC / filter / calibration without leaving the page,
 * but mutations stay gated to the streams page so unintended edits can't
 * happen from a side tab.
 */
export function StreamViewDialog({ mongoStreamId, open, onOpenChange }: StreamViewDialogProps) {
  const { data, isLoading } = useStreamForEdit(mongoStreamId);
  const stream: V2Stream | null = data?.stream || null;

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="3xl"
      title="Stream"
      description={
        <>
          Read-only view. Edit on{' '}
          <a href="/operations/streams" className="text-atek-blue hover:underline">
            operations / streams
          </a>.
        </>
      }
      onSubmit={() => Promise.resolve()}
      hideSave
      cancelLabel="Close"
    >
        {isLoading || !stream ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="space-y-6 text-sm">
            <Section title="Basic">
              <Row label="Name">{stream.name || '—'}</Row>
              <Row label="Probe Serial">{stream.probe_serial || '—'}</Row>
              <Row label="Probe Type">{stream.probe_type ?? '—'}</Row>
              <Row label="IPSO ID">{stream.ipso_id || '—'}</Row>
              <Row label="Description">{stream.description || '—'}</Row>
              <Row label="OneWire MAC">{stream.onewire_mac || '—'}</Row>
            </Section>

            <Section title="Reading">
              <Row label="Current Value">{nonNull(stream.value)}</Row>
              <Row label="Offset">{nonNull(stream.offset)}</Row>
              <Row label="Uncertainty">{nonNull(stream.uncertainty)}</Row>
              <Row label="Rounding Decimals">{nonNull(stream.rounding_decimals)}</Row>
              <Row label="Calibration Batch">{stream.calibration_batch || '—'}</Row>
            </Section>

            <Section title="Polynomial">
              {hasAnyValue(stream.polynomial) ? (
                <div className="grid grid-cols-5 gap-2">
                  {(['x1', 'x2', 'x3', 'x4', 'x5'] as const).map((k) => (
                    <Row key={k} label={k}>{nonNull(stream.polynomial?.[k])}</Row>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">No polynomial in database.</span>
              )}
            </Section>

            <Section title="ADC">
              {hasAnyValue(stream.adc) ? (
                <>
                  <Row label="Threshold">{nonNull(stream.adc?.threshold)}</Row>
                  <Row label="Resolution">{nonNull(stream.adc?.resolution)}</Row>
                  <Row label="Resistance">{nonNull(stream.adc?.resistance)}</Row>
                </>
              ) : (
                <span className="text-muted-foreground">No ADC config in database.</span>
              )}
            </Section>

            <Section title="Steinhart–Hart">
              {hasAnyValue(stream.steinharthart) ? (
                <>
                  <Row label="A">{nonNull(stream.steinharthart?.A)}</Row>
                  <Row label="B">{nonNull(stream.steinharthart?.B)}</Row>
                  <Row label="C">{nonNull(stream.steinharthart?.C)}</Row>
                </>
              ) : (
                <span className="text-muted-foreground">No Steinhart–Hart config in database.</span>
              )}
            </Section>

            <Section title="Filter">
              {hasAnyValue(stream.filter) ? (
                <>
                  <Row label="Min">{nonNull(stream.filter?.min)}</Row>
                  <Row label="Max">{nonNull(stream.filter?.max)}</Row>
                </>
              ) : (
                <span className="text-muted-foreground">No filter in database.</span>
              )}
            </Section>
          </div>
        )}
    </EntityFormDialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="font-semibold text-muted-foreground uppercase text-xs tracking-wide mb-2">{title}</h3>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-muted-foreground min-w-[140px]">{label}:</span>
      <span className="font-mono">{children}</span>
    </div>
  );
}

function nonNull(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return String(v);
}

function hasAnyValue(obj: Record<string, unknown> | null | undefined): boolean {
  if (!obj) return false;
  return Object.values(obj).some((v) => v !== null && v !== undefined);
}
